import { useState } from "react";
import { Search, Armchair, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import MyTableMap from "./MyTableMap";

// `unlockAt` is a datetime-local string ("2026-10-18T13:00") in the same wall-clock
// convention as the event date elsewhere in this app -- no timezone suffix, compared
// via `new Date()` which treats it as local time, matching how guests read it.
function formatUnlock(unlockAt) {
  const d = new Date(unlockAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("it-IT", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
}

export default function TableFinder({ slug, locked, unlockAt, title = "Dove sei seduto?", subtitle = "Inserisci il tuo nome per scoprire il tuo tavolo.", suggestions }) {
  const [nome, setNome] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSearch = async (value) => {
    if (value.trim().length < 2) return;
    trackEvent(slug, "table_search");
    setLoading(true);
    try {
      const r = await api.get(`/public/${slug}/my-table`, { params: { nome: value } });
      setResults(r.data.results);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const search = (e) => {
    e.preventDefault();
    runSearch(nome);
  };

  // Lets a landing-page demo offer one-click example names instead of asking
  // a visitor to type a stranger's name into an empty box -- same search path,
  // just pre-filled.
  const tryExample = (name) => {
    setNome(name);
    runSearch(name);
  };

  if (locked) {
    const unlockLabel = formatUnlock(unlockAt);
    return (
      <section className="py-20 sm:py-28 px-6 sm:px-12 bg-white" data-testid="table-finder-section-locked">
        <div className="max-w-xl mx-auto text-center">
          <p className="eyebrow mb-4">Il mio tavolo</p>
          <h2 className="font-wed text-3xl sm:text-4xl mb-4">Dove sei seduto?</h2>
          <div className="relative rounded-2xl overflow-hidden border" style={{ borderColor: "#e3ddd2" }}>
            <div aria-hidden className="pointer-events-none select-none blur-md opacity-50 p-8">
              <div className="flex gap-3">
                <div className="input-wed flex-1 text-left text-neutral-400">Il tuo nome e cognome</div>
                <div className="btn-wed !px-5"><Search size={18} /></div>
              </div>
              <div className="mt-8 border rounded-2xl p-5 flex items-start gap-4 text-left" style={{ borderColor: "#e3ddd2" }}>
                <div className="rounded-full p-3 shrink-0" style={{ background: "color-mix(in srgb, var(--wed-primary) 15%, white)" }}>
                  <Armchair size={22} style={{ color: "var(--wed-primary)" }} />
                </div>
                <div>
                  <p className="font-medium">Nome Cognome</p>
                  <p className="font-wed text-2xl mt-1" style={{ color: "var(--wed-primary)" }}>12</p>
                </div>
              </div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6">
              <Lock size={24} style={{ color: "var(--wed-primary)" }} />
              <p className="font-medium">
                {unlockLabel ? `Si sbloccherà il ${unlockLabel}` : "Si sbloccherà all'inizio del matrimonio"}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 sm:py-28 px-6 sm:px-12 bg-white" data-testid="table-finder-section">
      <div className="max-w-xl mx-auto text-center">
        <p className="eyebrow mb-4">Il mio tavolo</p>
        <h2 className="font-wed text-3xl sm:text-4xl mb-4">{title}</h2>
        <p className="text-neutral-500 mb-10 text-sm">{subtitle}</p>
        <form onSubmit={search} className="flex gap-3">
          <input
            className="input-wed flex-1"
            placeholder="Il tuo nome e cognome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            data-testid="table-finder-input"
          />
          <button type="submit" className="btn-wed !px-5 flex items-center gap-2" disabled={loading} data-testid="table-finder-submit">
            <Search size={18} />
          </button>
        </form>
        {suggestions?.length > 0 && (
          <div className="flex items-center justify-center gap-2 flex-wrap mt-4" data-testid="table-finder-suggestions">
            <span className="text-xs text-neutral-400">Prova con:</span>
            {suggestions.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => tryExample(name)}
                className="text-xs bg-[#faf6ee] border rounded-full px-3 py-1.5 hover:border-[var(--wed-primary)] transition-colors duration-150"
                style={{ borderColor: "#e3ddd2" }}
                data-testid={`table-finder-suggestion-${name.replace(/\s+/g, "-")}`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {results !== null && (
          <div className="mt-8 space-y-4 text-left" data-testid="table-finder-results">
            {results.length === 0 && (
              <p className="text-center text-neutral-500" data-testid="table-finder-no-results">
                Nessun invitato trovato con questo nome. Controlla l'ortografia o contatta gli sposi.
              </p>
            )}
            {results.map((r, i) => (
              <div key={i} className="border rounded-2xl p-5 flex items-start gap-4" style={{ borderColor: "#e3ddd2" }} data-testid={`table-result-${i}`}>
                <div className="rounded-full p-3 shrink-0" style={{ background: "color-mix(in srgb, var(--wed-primary) 15%, white)" }}>
                  <Armchair size={22} style={{ color: "var(--wed-primary)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{r.nome}</p>
                  {r.numero_tavolo != null ? (
                    <>
                      <p className="font-wed text-2xl mt-1" style={{ color: "var(--wed-primary)" }}>
                        {r.numero_tavolo}
                      </p>
                      {r.compagni.length > 0 && (
                        <p className="text-sm text-neutral-500 mt-2">Con: {r.compagni.join(", ")}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-neutral-500 mt-1">Tavolo non ancora assegnato</p>
                  )}
                  {r.layout && <MyTableMap layout={r.layout} myName={r.nome} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
